import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MON_MODULES } from '@monsido/core/constants/mon-modules.constant';
import { LabelRepo } from '@monsido/modules/endpoints/api/label.repo';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { DomainService } from 'app/services/entities/domain/domain.service';
import { CoreDomainService } from '../../../core/services/domain/core-domain.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { PipeService } from '../../../filters/pipe-transformation.service';
import { ScanDetailsDialogComponent } from '@client/app/components/dialogs/scan-details/scan-details.component';
import { CollectionInterface, Label } from '@monsido/angular-shared-components';
import { Domain } from '@monsido/modules/models/api/domain';
import { BaseCurrentSort } from '@client/app/blocks/base/base.controller';
import { User } from '@monsido/modules/models/api/user';
import { DomainGroup } from '@monsido/modules/models/api/domain-group';
import { FormDomainScriptGuideComponent } from '@client/app/forms/domain-script-guide/domain-script-guide.component';
import { AdminDomainsService } from '@client/app/pages/admin/domains/admin-domains-service/admin-domains.service';
import { FormDomainComponent } from '@client/app/forms/domain/form-domain.component';
import { FormDomainExcludeIpComponent } from '@client/app/forms/domain-exclude-ip/domain-exclude-ip.component';
import { cloneDeep } from 'lodash';
import {
    DomainDataPrivacySettingsComponent,
} from '@client/app/modules/data-privacy/components/domain-settings/domain-data-privacy-settings.component';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { Customer } from '@monsido/ng2/modules/models/api/customer';
import { AccountService } from '@client/app/services/entities/account/account.service';
import {
    FormDomainGroupsEditComponent,
} from '@client/app/forms/domain/steps/domain_groups/edit/form-domain-groups-edit.component';
import {
    DomainGroupUpdateInfoService,
} from '@client/app/services/entities/domain-group/domain-group-update-info/domain-group-update-info.service';
import { DomainGroupService } from '@client/app/services/entities/domain-group/domain-group.service';
import { LabelsEntity } from '@monsido/modules/models/api/interfaces/domain.interface';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';

type ComputedDomain = Domain & {
    crawl_history: Domain['crawl_history'] & {
        qa_count: number;
        seo_count: number;
    }
};

@Component({
    selector: 'projects-and-domains',
    standalone: false,
    templateUrl: './projects-and-domains.component.html',
})
export class ProjectsAndDomainsComponent implements OnInit, OnDestroy {
    @ViewChild('domainStatusTooltipTemplate', { static: true }) domainStatusTooltipTemplate!: TemplateRef<any>;

    @Input() progress: Promise<CollectionInterface<Domain>> | null = null;
    @Input() domainObservable?: Observable<ComputedDomain[]>;
    @Input() sort: BaseCurrentSort = {
        by: 'favorite',
        direction: 'asc',
    };
    @Input() isFiltersOpen = false;
    @Input() filters: any;
    @Input() availableFilters: any;

    @Output() onPageChange = new EventEmitter<number>();
    @Output() onPerPageChange = new EventEmitter<string>();
    @Output() getPage = new EventEmitter<boolean>();
    @Output() onSort = new EventEmitter<{ direction: 'asc' | 'desc'; by: string; }>();
    @Output() onFiltersChanges = new EventEmitter<any>();
    @Output() isFiltersOpenChange = new EventEmitter<boolean>();
    @Output() filtersChange = new EventEmitter<any>();
    @Output() createDomain = new EventEmitter<Domain | undefined>();

    customer: Customer | null = null;
    domains: ComputedDomain[] = [];
    hoverDomain: number | null = null;
    showLabels = false;
    switchDomain: number | null = null;
    switchDomainGroup: number | null = null;
    showLabel: Record<number, boolean> = {};
    user: User | null | undefined = null;
    allLabels: Label[] = [];
    colSpanSpace: number = 0;
    scriptSetupGuideIsAvailable = false;
    hasOnDemandScans = false;
    canEditDomainLabels = false;
    updatingDomains: Record<number, boolean> = {};
    updatingDomainGroups: Record<number, Set<number>> = {};
    destroyed = false;
    private onDestroy$ = new Subject<void>();

    constructor (
        private labelRepo: LabelRepo,
        private stateService: StateService,
        private monEventService: MonEventService,
        private sessionService: SessionService,
        private activeFeatureService: ActiveFeatureService,
        private domainService: DomainService,
        private coreDomainService: CoreDomainService,
        private loginService: LoginService,
        private pipeService: PipeService,
        private cdRef: ChangeDetectorRef,
        private adminDomainsService: AdminDomainsService,
        private translateService: TranslateService,
        private monModalService: MonModalService,
        private accountService: AccountService,
        private domainGroupService: DomainGroupService,
        private domainGroupUpdateInfoService: DomainGroupUpdateInfoService,
        private domainRepoService: DomainRepoService,
    ) {}

    ngOnDestroy (): void {
        this.destroyed = true;
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    ngOnInit (): void {
        this.customer = this.sessionService.customer;
        this.canEditDomainLabels = this.sessionService.hasRole('customer_admin');
        this.sessionService.domain = null;
        this.coreDomainService.changeDomain();
        this.showLabels = this.activeFeatureService.isFeatureActive('labels');
        this.scriptSetupGuideIsAvailable = this.activeFeatureService.isFeatureActive('script_setup_guide');
        this.hasOnDemandScans = this.activeFeatureService.isFeatureActivePure('on_demand_scans');
        this.showLabel = [];

        this.loginService.getUser().then(
            (user: any) => {
                this.user = user;
            },
            () => {
                this.user = this.sessionService.me;
                this.colSpanSpace = 0;
            },
        );

        this.getLabels();

        if (this.domainObservable) {
            this.domainObservable.subscribe(domains => {
                this.domains = domains;
                this.requestGroupUpdatingStatus();
            });
        }
    }

    getLabels () {
        this.labelRepo.getLabels().then((data: any) => {
            this.allLabels = data;
        }, () => {});
    }

    toSection (event: Event | null, type: string, domain: ComputedDomain, domainGroup?: DomainGroup | null): void {
        if (event) {
            event.stopPropagation();
        }
        let state: string;
        const params: { domainId: number, domain_group?: number } = {
            domainId: domain.id,
        };

        switch (type) {
            case 'policy':
                state = 'base.customer.domain.policy.dashboard';
                break;
            case 'qa':
                state = 'base.customer.domain.qa.summary';
                break;
            case 'accessibility':
                state = 'base.customer.domain.accessibility.summary';
                break;
            case 'seo':
                state = 'base.customer.domain.seo.summary';
                break;
            case 'pages':
                state = 'base.customer.domain.pages';
                break;
            case 'documents':
                state = 'base.customer.domain.inventory.content.documents';
                break;
            case 'dataprivacy':
                state = 'base.customer.domain.data-privacy.summary';
                break;
            default:
                state = 'base.customer.domain.dashboard';
        }

        this.switchDomain = domain.id;
        if (domainGroup) {
            this.switchDomainGroup = domainGroup.id;
            params.domain_group = domainGroup.id;
            this.coreDomainService.setDomainGroup(domainGroup);
        } else {
            this.coreDomainService.removeDomainGroup();
        }

        this.stateService
            .go(state, params)
            .then(() => {
                this.coreDomainService.changeDomain();
            }, () => {})
            .finally(() => {
                this.switchDomain = null;
                this.switchDomainGroup = null;
            });
    }

    goTo (domain: ComputedDomain, domainGroup?: any): void {
        this.toSection(null, 'dashboard', domain, domainGroup);
    }

    domainAccCheckCount (accChecks: any): number {
        let total = 0;
        for (const check in accChecks) {
            if (['error', 'warning', 'review'].indexOf(check) !== -1) {
                total += accChecks[check] || 0;
            }
        }
        return total;
    }

    updateDomain (domain: ComputedDomain): void {
        const params: any = {};
        params.labels = (domain.labels || []).map((value: LabelsEntity, index: number, array: LabelsEntity[]) => {
            return value.id;
        });

        this.domainService.update(domain.id, params);
    }

    openScanDetails (domain: ComputedDomain, event: Event): void {
        const domainObservable = new BehaviorSubject(domain);
        event.stopPropagation();
        if (this.domainObservable) {
            this.domainObservable.subscribe((domains: ComputedDomain[]) => {
                const nextDomain = domains.find((listDomain: ComputedDomain) => {
                    return listDomain.id === domain.id;
                });

                if (nextDomain) {
                    domainObservable.next(nextDomain);
                } else {
                    domainObservable.next(domain);
                }
            });
        }
        const params = {
            body: ScanDetailsDialogComponent,
            size: 'sm',
            classes: 'fade-animation middle center',
            data: {
                domainObservable: domainObservable,
            },
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    getUnavailableModuleCount (domain: ComputedDomain, domainGroup?: DomainGroup): number {
        if (this.hasDomainOrDomainGroupData(domain, domainGroup)) {
            return this.getNoDataTitleWidth();
        }
        return [
            this.hasAccessToSEOModule(domain, domainGroup),
            this.hasAccessToQAModule(domain, domainGroup),
            this.hasAccessToAccessibilityModule(domain, domainGroup),
            this.hasAccessToPoliciesModule(domain, domainGroup),
            this.hasAccessToDataPrivacyModule(domain, domainGroup),
        ].reduce((num: number, hasAccess: boolean) => {
            if (!hasAccess) {
                return num + 1;
            }
            return num;
        }, 1);
    }

    getNoDataWidth (): number {
        // not using helper function because they always return false on no data
        return [
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.seo),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.data_privacy),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.qa),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.policies),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.accessibility),
        ].reduce((num: number, hasAccess: boolean) => {
            if (!hasAccess) {
                return num - 1;
            }
            return num;
        }, 7);
    }

    private getNoDataTitleWidth (): number {
        return [
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.seo),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.data_privacy),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.qa),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.policies),
            this.pipeService.get('hasAccess')(this.user, MON_MODULES.accessibility),
        ].reduce((num: number, hasAccess: boolean) => {
            if (!hasAccess) {
                return num + 1;
            }
            return num;
        }, 1);
    }

    hasAccessToSEOModule (domain: ComputedDomain, domainGroup?: DomainGroup): boolean {
        if (!this.activeFeatureService.isFeatureActive('seo_all')) {
            return false;
        }
        if (this.hasDomainOrDomainGroupData(domain, domainGroup)) {
            return false;
        }
        return this.pipeService.get('hasAccess')(this.user, MON_MODULES.seo);
    }

    hasAccessToDataPrivacyModule (domain: ComputedDomain, domainGroup?: DomainGroup): boolean {
        if (!this.activeFeatureService.isFeatureActive('admin_data_privacy')) {
            return false;
        }
        if (this.hasDomainOrDomainGroupData(domain, domainGroup)) {
            return false;
        }
        return this.pipeService.get('hasAccess')(this.user, MON_MODULES.data_privacy);
    }

    hasAccessToQAModule (domain: ComputedDomain, domainGroup?: DomainGroup): boolean {
        if (!this.activeFeatureService.isFeatureActive('qa_module')) {
            return false;
        }
        if (this.hasDomainOrDomainGroupData(domain, domainGroup)) {
            return false;
        }
        return this.pipeService.get('hasAccess')(this.user, MON_MODULES.qa);
    }

    hasAccessToPoliciesModule (domain: ComputedDomain, domainGroup?: DomainGroup): boolean {
        if (!this.activeFeatureService.isFeatureActive('policies_all')) {
            return false;
        }
        if (this.hasDomainOrDomainGroupData(domain, domainGroup)) {
            return false;
        }
        return this.pipeService.get('hasAccess')(this.user, MON_MODULES.policies);
    }

    hasAccessToAccessibilityModule (domain: ComputedDomain, domainGroup?: DomainGroup): boolean {
        if (this.hasDomainOrDomainGroupData(domain, domainGroup)) {
            return false;
        }
        return this.pipeService.get('hasAccess')(this.user, MON_MODULES.accessibility);
    }

    hasDomainOrDomainGroupData (domain: ComputedDomain, domainGroup?: DomainGroup): boolean {
        return (domain && domain.crawl_status !== null) || Boolean(domainGroup && !domainGroup.created_at);
    }

    onHover (domain: ComputedDomain): void {
        this.showLabel[domain.id] = true;
        if (this.pipeService.get('hasDomainAccess')(domain)) {
            this.hoverDomain = domain.id;
        }
    }

    onHoverLeave (domain: ComputedDomain): void {
        this.showLabel[domain.id] = false;
        this.hoverDomain = null;
    }

    trackByDomainId (index: number, item: ComputedDomain): number {
        return item.id;
    }

    trackByDomainGroupId (index: number, item: DomainGroup): number {
        return item.id;
    }

    goToDomain (domain: Domain, domainGroup?: DomainGroup): void {
        this.switchDomain = domain.id;
        const params: any = {
            domainId: domain.id,
        };
        if (domainGroup) {
            this.switchDomainGroup = domainGroup.id;
            params.domain_group = domainGroup.id;
        }
        const page = 'base.customer.domain.dashboard';

        this.stateService
            .go(page, params)
            .then(() => {
                this.coreDomainService.changeDomain();
            }, () => {})
            .finally(() => {
                this.switchDomain = null;
                this.switchDomainGroup = null;
            });
    }

    openScriptGuide (domain: Domain): void {
        const params = {
            body: FormDomainScriptGuideComponent,
            size: 'md',
            data: { domain: domain },
            cdRef: this.cdRef,
        };

        const options = {
            params,
            callback: (updatedDomain: Domain) => {
                if (updatedDomain) {
                    this.monEventService.run('change-editedScriptBlock-status');
                    this.adminDomainsService.updateDomainSettings(domain);
                }
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    editDomain (selectedDomain: Domain, index: number): void {
        const params = {
            body: FormDomainComponent,
            size: 'md',
            data: { domain: selectedDomain },
            cdRef: this.cdRef,
        };

        const options = {
            params,
            callback: () => {
                this.updatingDomains[selectedDomain.id] = true;
                this.domainService.get(selectedDomain.id)
                    .then((updatedDomain: any) => {
                        if (this.domains[index] && this.domains[index].id === updatedDomain.id) {
                            this.domains[index] = updatedDomain;
                        }
                    }, () => {})
                    .finally(() => {
                        delete this.updatingDomains[selectedDomain.id];
                    });
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    editExcludedIps (domain: Domain): void {
        const params = {
            body: FormDomainExcludeIpComponent,
            size: 'md',
            data: { domainId: domain.id },
            cdRef: this.cdRef,
        };

        const options = {
            params,
            callback: () => {
                this.getPage.emit();
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    startOnDemandScan (domain: Domain): void {
        if (domain.crawl_status) {
            return;
        }
        this.monModalService.confirm(this.translateService.getString('Are you sure you want to start on-demand scan?'), {}).then(() => {
            this.domainService.rescan(domain.id).then(() => {
                this.getPage.emit();
            }, () => {});
        }, () => {});
    }

    cloneDomain (domain: Domain): void {
        this.domainRepoService.get(domain.id).then((fullDomain: Domain) => {
            const clone = cloneDeep(fullDomain);

            clone.domain_users?.forEach((user: any) => {
                delete user.id;
                return user;
            });
            clone.link_excludes = clone.link_excludes?.map((exclude: any) => {
                delete exclude.id;
                return exclude;
            }) || [];
            clone.path_constraints = clone.path_constraints?.map((constraint: any) => {
                delete constraint.id;
                return constraint;
            }) || [];
            clone.domain_groups = [];

            delete clone.scan?.origTime;
            delete clone.crawl_history;
            // eslint-disable-next-line
            // @ts-ignore
            delete clone.updated_at;
            // eslint-disable-next-line
            // @ts-ignore
            delete clone.created_at;
            delete clone.crawl_status;
            // eslint-disable-next-line
            // @ts-ignore
            delete clone.id;
            // eslint-disable-next-line
            // @ts-ignore
            delete clone.url;
            // eslint-disable-next-line
            // @ts-ignore
            delete clone.title;

            this.createDomain.emit(new Domain(clone));
        }, () => {});
    }

    openDataPrivacy (domain: Domain): void {
        const params = {
            body: DomainDataPrivacySettingsComponent,
            size: 'md',
            data: {
                domain: domain,
            },
            cdRef: this.cdRef,
        };

        const options = {
            params,
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    async updateDomainGroup (domain: Domain): Promise<void> {
        const domainId = domain.id;

        this.updateDomainRunningGroups(domainId, true);

        const groupIds = (domain.domain_groups || []).map((group: any) => group.id);

        if (!this.updatingDomainGroups[domainId]) {
            this.updatingDomainGroups[domainId] = new Set();
        }
        groupIds.forEach((id: number) => {
            this.updatingDomainGroups[domainId].add(id);
        });

        await this.domainGroupService.updateDomainGroupRules(domainId);

        return new Promise(
            resolve => this.delayCheckGroupsUpdatingStatus(resolve, domainId, groupIds),
        );
    }

    onConfirmDelete (domain: Domain): void {
        const msg =
            this.translateService.getString('You are about to delete the domain') +
            ' <span class=\'text-link\'>' +
            domain.title +
            '</span> ' +
            this.translateService.getString('please type') +
            ' <code>Delete</code> ' +
            this.translateService.getString('to confirm this action');

        const fullMsg = msg +
            '<div class=\'alert alert-danger inner-t-mini inner-b-mini inner-l-mini inner-r-mini outer-b-none outer-t-medium\'>' +
            this.translateService.getString('By deleting this domain all domain groups related to this domain, will also be deleted') +
            '</div>';

        this.monModalService.prompt(fullMsg, {}).then((result: any) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }
            this.domainService.destroy(domain.id).then(() => {
                if (domain.crawl_history) {
                    this.updateCustomer(domain);
                }
                this.getPage.emit();
            }, () => {});
        }, () => {});
    }

    stopCurrentCrawl (domain: Domain): void {
        this.domainService.stopCrawl(domain.id).then(() => {
            this.getPage.emit();
        }, () => {});
    }


    updateCustomer (domain: Domain): void {
        if (!this.customer) {
            return;
        }

        const updatedDocumentsCount = this.customer.documents_count - (domain.crawl_history?.documents_count || 0);
        const updatedHtmlPagesCount = this.customer.html_pages_count - (domain.crawl_history?.page_count || 0);
        const updatedCustomer = cloneDeep(this.customer);
        updatedCustomer.documents_count = updatedDocumentsCount;
        updatedCustomer.html_pages_count = updatedHtmlPagesCount;

        if (this.customer.documents_count !== updatedDocumentsCount || this.customer.html_pages_count !== updatedHtmlPagesCount) {
            this.accountService
                .update(updatedCustomer)
                .then((data: any) => {
                    this.customer = data;
                }, () => {});
        }
    }

    editDomainGroup (domain: Domain, domainGroup: DomainGroup): void {
        const params = {
            body: FormDomainGroupsEditComponent, // Would need FORM_CONSTANT.FORM_DOMAIN_GROUPS_EDIT
            size: 'lg',
            data: { domainGroup: domainGroup, domain: domain },
            cdRef: this.cdRef,
        };

        const options = {
            params,
            callback: (updatedDomainGroup: any) => {
                if (updatedDomainGroup) {
                    this.getPage.emit();
                }
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }

    async updateSingleDomainGroup (domainId: number, groupId: number): Promise<void> {
        this.updateDomainRunningGroups(domainId, true);

        if (!this.updatingDomainGroups[domainId]) {
            this.updatingDomainGroups[domainId] = new Set();
        }

        this.updatingDomainGroups[domainId].add(groupId);

        await this.domainGroupService.updateSingleDomainGroupRules(domainId, groupId);

        return new Promise(
            resolve => this.delayCheckGroupsUpdatingStatus(resolve, domainId, [groupId]),
        );
    }

    onConfirmDeleteGroup (domain: Domain, domainGroup: DomainGroup): void {
        const msg =
            this.translateService.getString('You are about to delete the domain group') +
            ' <span class=\'text-link\'>' +
            domainGroup.title +
            '</span> ' +
            this.translateService.getString('please type') +
            ' <code>Delete</code> ' +
            this.translateService.getString('to confirm this action');

        this.monModalService.prompt(msg, {}).then((result: any) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }

            this.domainGroupService.destroy(domain.id, domainGroup.id).then(() => {
                if (domain.crawl_history) {
                    this.updateCustomer(domain);
                }
                this.getPage.emit();
            }, () => {});
        }, () => {});
    }

    private delayCheckGroupsUpdatingStatus (resolve: any, domainId: number, groupIds: number[]): void {
        setTimeout(() => {
            if (!this.destroyed) {
                this.checkGroupsUpdatingStatus(domainId, groupIds);
            }
            resolve();
        }, 2000); // gives 2s to Backend to react
    }

    private requestGroupUpdatingStatus (): void {
        this.domains.forEach((domain: any) => {
            if (domain.running_groups) {
                const groupIds = domain.domain_groups.map((group: any) => group.id);
                this.checkGroupsUpdatingStatus(domain.id, groupIds);
            }
        });
    }

    private checkGroupsUpdatingStatus (domainId: number, groupIds: number[]): void {
        this.domainGroupUpdateInfoService
            .getUpdateStatus(domainId, groupIds, this.onDestroy$ as unknown as Subject<unknown>)
            .subscribe((updatingGroupIds: any) => {
                if (!this.destroyed) {
                    if (updatingGroupIds.size) {
                        this.updatingDomainGroups[domainId] = new Set(updatingGroupIds);
                    } else {
                        delete this.updatingDomainGroups[domainId];
                        this.updateDomainRunningGroups(domainId, false);
                    }

                    // trigger digest
                    this.domains = this.cloneCollection(this.domains);
                }
            });
    }

    private updateDomainRunningGroups (domainId: number, isRunning: boolean): void {
        this.domains.forEach((domain: any) => {
            if (domain.id === domainId) {
                domain.running_groups = isRunning;
            }
        });
        this.domains = this.cloneCollection(this.domains);
    }

    private cloneCollection (collection: any): any {
        const { total, currentPage, flattenDomains, pageHits, perPage } = collection;
        const result = cloneDeep(collection);
        result.total = total;
        result.currentPage = currentPage;
        result.pageHits = pageHits;
        result.perPage = perPage;
        result.flattenDomains = flattenDomains;
        return result;
    }
}
